/**
 * Creates a function that is restricted to invoking func once.
 * Repeat calls to the function return the value of the first invocation.
 * Failures are not cached: a sync throw or rejected promise allows a retry
 * on the next call, so a transient error can't poison the cache.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function once<T extends (...args: any[]) => any>(fn: T): T {
  let called = false
  let result: ReturnType<T>

  return ((...args: Parameters<T>) => {
    if (!called) {
      called = true
      try {
        result = fn(...args)
      } catch (error) {
        called = false
        throw error
      }
      if ((result as unknown) instanceof Promise) {
        ;(result as Promise<unknown>).catch(() => {
          called = false
        })
      }
    }
    return result
  }) as T
}

/**
 * Creates a memoized function that caches results based on the first argument.
 * Subsequent calls with the same first argument return the cached result.
 * Rejected promises are evicted from the cache so the next call retries.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function memoize<T extends (...args: any[]) => any>(fn: T): T {
  const cache = new Map<unknown, ReturnType<T>>()

  return ((...args: Parameters<T>) => {
    const key = args[0]
    if (cache.has(key)) {
      return cache.get(key)
    }
    const result = fn(...args)
    cache.set(key, result)
    if ((result as unknown) instanceof Promise) {
      ;(result as Promise<unknown>).catch(() => {
        cache.delete(key)
      })
    }
    return result
  }) as T
}
